import {
	Component,
	EventEmitter,
	forwardRef,
	Input,
	OnChanges,
	OnInit,
	Output,
	SimpleChanges,
} from '@angular/core';
import {
	ControlContainer,
	ControlValueAccessor,
	FormBuilder,
	FormControl,
	FormGroup,
	FormGroupDirective,
	NG_VALUE_ACCESSOR,
} from '@angular/forms';
import { noop } from 'rxjs';

@Component({
	selector: 'seira-transfer-list',
	providers: [
		{
			provide: NG_VALUE_ACCESSOR,
			useExisting: forwardRef(() => TransferListComponent),
			multi: true,
		},
	],
	viewProviders: [
		{ provide: ControlContainer, useExisting: FormGroupDirective },
	],
	templateUrl: './transfer-list.component.html',
	styleUrls: ['./transfer-list.component.scss'],
})
export class TransferListComponent<T extends Record<string, any>>
	implements ControlValueAccessor, OnChanges, OnInit
{
	@Input() inputList: T[] = [];
	@Input() outputList: T[] = [];
	@Output() outputListChange = new EventEmitter<T[]>();
	@Input() inputLabelClass = 'fw-semibold mb-2';
	@Input() inputLabel = '';
	@Input() outputLabel = '';
	@Input() labelProperty = 'label';
	@Input() valueProperty = 'value';
	@Input() loading = false;
	@Input() disabled = false;
	@Input() required = false;
	@Input() formControlName!: string;

	dirty = false;

	form: FormGroup = this.formBuilder.group({
		inputFilter: new FormControl(''),
		outputFilter: new FormControl(''),
	});

	control: FormControl = new FormControl();

	constructor(
		private readonly formBuilder: FormBuilder,
		private controlContainer: ControlContainer
	) {}

	ngOnInit(): void {
		const parentForm = this.controlContainer.control;
		if (parentForm && this.formControlName) {
			this.control = parentForm.get(this.formControlName) as FormControl;
		}
	}

	private onChange: (_: T[]) => void = noop;
	private onTouched: () => void = noop;

	writeValue(value: T[]): void {
		this.outputList = value || [];
		this.sortAlphabetically();

		if (!value?.length) {
			this.dirty = false;
		}
	}

	registerOnChange(fn: (_: T[]) => void): void {
		this.onChange = fn;
	}

	registerOnTouched(fn: () => void): void {
		this.onTouched = fn;
	}

	setDisabledState(isDisabled: boolean): void {
		this.disabled = isDisabled;
	}

	get filteredInputList() {
		const search = this.form.get('inputFilter')?.value;
		return this.inputList.filter(item => this.searchFilter(item, search));
	}

	get filteredOutputList() {
		const search = this.form.get('outputFilter')?.value;
		return this.outputList.filter(item => this.searchFilter(item, search));
	}

	searchFilter(item: T, search: string) {
		return item[this.labelProperty]
			.toLowerCase()
			.includes(search.toLowerCase());
	}

	ngOnChanges(changes: SimpleChanges) {
		if (changes['inputList'] || changes['outputList']) {
			const inputValues = this.inputList.reduce(
				(acc, item) => {
					acc[item[this.labelProperty]] = new FormControl(false);
					return acc;
				},
				{} as Record<string, FormControl>
			);
			const outputValues = this.outputList.reduce(
				(acc, item) => {
					acc[item[this.labelProperty]] = new FormControl(false);
					return acc;
				},
				{} as Record<string, FormControl>
			);

			for (const [key, value] of Object.entries({
				...inputValues,
				...outputValues,
			})) {
				if (!this.form.contains(key)) {
					this.form.addControl(key, value);
				}
			}
			this.sortAlphabetically();
		}
	}

	addSelectedItems() {
		const { inputFilter, outputFilter, ...items } = this.form.getRawValue();

		Object.entries(items).forEach(([label, value]) => {
			if (!value) return;

			const item = this.inputList.find(
				item => item[this.labelProperty] === label
			);

			if (!item) return;

			this.outputList.push(item);
			this.outputListChange.emit(this.outputList);
			this.onChange(this.outputList);
			this.inputList.splice(this.inputList.indexOf(item), 1);
			this.form.get(label)?.setValue(false);
			this.sortAlphabetically();
		});

		this.dirty = true;
		this.onTouched();
	}

	removeSelectedItems() {
		const { inputFilter, outputFilter, ...items } = this.form.getRawValue();

		Object.entries(items).forEach(([label, value]) => {
			if (!value) return;

			const item = this.outputList.find(
				item => item[this.labelProperty] === label
			);

			if (!item) return;

			this.inputList.push(item);
			this.outputList.splice(this.outputList.indexOf(item), 1);
			this.outputListChange.emit(this.outputList);
			this.onChange(this.outputList);
			this.form.get(label)?.setValue(false);
			this.sortAlphabetically();
		});

		this.dirty = true;
		this.onTouched();
	}

	addAllItems() {
		const novos = this.inputList.filter(
			item => !this.outputList.includes(item)
		);
		this.outputList = [...this.outputList, ...novos];
		this.inputList = [];
		this.outputListChange.emit(this.outputList);
		this.onChange(this.outputList);
		this.sortAlphabetically();
		this.dirty = true;
		this.onTouched();
	}

	removeAllItems() {
		this.inputList.push(...this.outputList);
		this.sortAlphabetically();
		this.outputList = [];
		this.outputListChange.emit(this.outputList);
		this.onChange(this.outputList);
		this.dirty = true;
		this.onTouched();
	}

	sortAlphabetically() {
		this.inputList.sort((a, b) =>
			a[this.labelProperty].localeCompare(b[this.labelProperty])
		);
		this.outputList.sort((a, b) =>
			a[this.labelProperty].localeCompare(b[this.labelProperty])
		);
	}

	get showRequiredMessage() {
		return (
			(this.required && this.dirty && !this.outputList.length) ||
			(this.control?.touched && this.control?.hasError('required'))
		);
	}

	public resetInterno(): void {
		this.dirty = false;
		this.required = false;
		this.form.reset({
			inputFilter: '',
			outputFilter: '',
		});

		for (const key of Object.keys(this.form.controls)) {
			if (key !== 'inputFilter' && key !== 'outputFilter') {
				this.form.get(key)?.setValue(false);
			}
		}
	}
}
